<template>
  <div class="filial-provedor-fiscal-frame">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <span class="text-body-secondary small">
        <i class="bi bi-plug-fill me-1"></i>
        Credenciais do provedor de emissão fiscal (1 por Filial/Provedor/Ambiente)
      </span>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        :disabled="!idFilial"
        :title="!idFilial ? 'Salve a filial primeiro para adicionar credenciais' : 'Adicionar Credencial'"
        @click="abrirNovo"
      >
        <i class="bi bi-plus-lg me-1"></i>Adicionar
      </button>
    </div>

    <AdminTable
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma credencial de emissão cadastrada para esta filial."
      row-key="idFilialProvedorFiscal"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idFilialProvedorFiscal }}</span>
      </template>
      <template #apiKey="{ row }">
        <span class="font-monospace text-body-secondary">••••{{ row.apiKeyLast4 || '----' }}</span>
      </template>
      <template #status="{ row }">
        <span class="badge" :class="row.isAtivo ? 'bg-success' : 'bg-secondary'">{{ row.isAtivo ? 'Ativo' : 'Inativo' }}</span>
      </template>
      <template #acoes="{ row }">
        <button class="btn-icon text-primary me-2" @click="abrirEdicao(row)" title="Editar">
          <i class="bi bi-pencil-fill"></i>
        </button>
        <button class="btn-icon text-danger" @click="confirmarExclusao(row)" title="Excluir">
          <i class="bi bi-trash-fill"></i>
        </button>
      </template>
    </AdminTable>

    <ConfirmationModal
      ref="modalExclusao"
      titulo="Excluir Credencial"
      message="Tem certeza que deseja excluir esta credencial de emissão? A emissão fiscal desta filial poderá falhar."
      btn-confirmar-texto="Sim, Excluir"
      btn-cancelar-texto="Cancelar"
      :is-danger="true"
      @confirmar="executarExclusao"
    />

    <FilialProvedorFiscalModal
      v-model="modalOpen"
      :id-filial="idFilial"
      :credencial="credencialEmEdicao"
      @salvo="onSalvo"
      @show-notification="(p) => emit('show-notification', p)"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import api from '@/services/api'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import FilialProvedorFiscalModal from '@/components/organizacoes/FilialProvedorFiscalModal.vue'

const props = defineProps({
  idFilial: { type: Number, default: 0 }
})

const emit = defineEmits(['show-notification'])

const lista = ref([])
const loading = ref(false)
const modalExclusao = ref(null)
const credencialParaExcluir = ref(null)
const modalOpen = ref(false)
const credencialEmEdicao = ref(null)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'idFilialProvedorFiscal', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'lstProvedor', label: 'Provedor', sortable: true },
  { field: 'lstAmbiente', label: 'Ambiente', sortable: true },
  { field: 'provedorProjectId', label: 'Projeto (ID)', sortable: true, class: 'font-monospace' },
  { field: 'apiKeyLast4', label: 'API Key', sortable: false, slotName: 'apiKey' },
  { field: 'isAtivo', label: 'Status', width: 110, align: 'center', sortable: true, slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: 110, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const buscarDados = async () => {
  if (!props.idFilial) {
    lista.value = []
    return
  }

  loading.value = true
  try {
    const response = await api.get(`/FilialProvedoresFiscais?idFilial=${props.idFilial}`)
    lista.value = response.data
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar credenciais de emissão.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const abrirNovo = () => {
  if (!props.idFilial) return
  credencialEmEdicao.value = null
  modalOpen.value = true
}

const abrirEdicao = (row) => {
  credencialEmEdicao.value = row
  modalOpen.value = true
}

const onSalvo = async () => {
  modalOpen.value = false
  await buscarDados()
}

const confirmarExclusao = (row) => {
  credencialParaExcluir.value = row
  modalExclusao.value?.show()
}

const executarExclusao = async () => {
  try {
    await api.delete(`/FilialProvedoresFiscais/${credencialParaExcluir.value.idFilialProvedorFiscal}`)
    emit('show-notification', { message: 'Credencial excluída com sucesso.', type: 'success' })
    await buscarDados()
  } catch (error) {
    const data = error.response?.data
    const msg = data?.message || data?.title || 'Erro ao excluir credencial.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    if (modalExclusao.value && modalExclusao.value.hide) {
      modalExclusao.value.hide()
    }
  }
}

watch(() => props.idFilial, (novo) => {
  if (novo) buscarDados()
  else lista.value = []
})

onMounted(() => { buscarDados() })

defineExpose({ buscarDados })
</script>

<style scoped>
.btn-icon { background: none; border: none; color: #6c7d8e; padding: 0.4rem; transition: 0.2s; }
.btn-icon:hover { color: var(--bs-body-emphasis); }
</style>
